/**
 * KnowledgePanel — KNOWLEDGE biome (Mind Vault): searchable personal knowledge,
 * lifelogs, and collector transcripts. Live via sage-personal-mcp
 * (universal_personal_search). Search-first; output shapes vary so access is
 * defensive. Distinct from PersonalPanel (recent comms) and Timeline (events).
 */
import { useMemo, useState } from "react";
import { Brain, Search, X, FileText } from "lucide-react";
import McpCard from "../components/McpCard";
import { useMcpTool } from "../lib/useMcpTool";
import { PanelScroll } from "./ClientsPanel";
import { setChatContext } from "../lib/chatContext";

interface Hit { title?: string; text?: string; content?: string; content_snippet?: string; snippet?: string; summary?: string; preview?: string; source?: string; type?: string; ts?: string; timestamp?: string; date?: string; created_at?: string; occurred_at?: number | string; score?: number; [k: string]: unknown }

// universal_personal_search returns { results: [{ source, row: {...} }] }; flatten row up.
function hitsFrom(data: unknown): Hit[] {
  if (Array.isArray(data)) return data as Hit[];
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    for (const k of ["results", "hits", "rows", "items", "matches", "documents"]) {
      const arr = o[k];
      if (Array.isArray(arr)) {
        return arr.map((it) => {
          const r = it as Record<string, unknown>;
          if (r && typeof r.row === "object" && r.row) return { ...(r.row as Hit), source: (r.source as string) ?? (r.row as Hit).source };
          return r as Hit;
        });
      }
    }
  }
  return [];
}
const bodyOf = (h: Hit) => h.content_snippet ?? h.snippet ?? h.summary ?? h.preview ?? h.text ?? h.content ?? "";
const titleOf = (h: Hit) => h.summary ?? h.title ?? (bodyOf(h) ? bodyOf(h).slice(0, 80) : "(result)");
function tsLabel(h: Hit): string {
  const v = h.occurred_at ?? h.ts ?? h.timestamp ?? h.created_at ?? h.date;
  if (v == null) return "";
  if (typeof v === "number") return new Date(v * (v < 1e12 ? 1000 : 1)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return String(v).slice(0, 10);
}

const QUICK = ["Toast menu build", "Chapin's network", "Crown & Anchor", "housing decision", "trading engine"];

export default function KnowledgePanel() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const res = useMcpTool<unknown>("personal", "universal_personal_search", { query: submitted, limit: 30 }, { enabled: !!submitted, timeoutMs: 25_000 });
  const hits = useMemo(() => hitsFrom(res.data), [res.data]);

  const submit = (q: string) => { setQuery(q); setSubmitted(q.trim()); };

  return (
    <PanelScroll accentVar="--biome-mindvault" title="Knowledge" subtitle="Mind Vault · lifelogs + transcripts · sage-personal">
      <form onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim()); }} className="flex items-center gap-2 rounded-xl border bg-surface px-3 h-10">
        <Search size={15} className="text-text-dim shrink-0" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your knowledge, lifelogs, and notes…" className="flex-1 bg-transparent text-text placeholder-text-dim outline-none text-body-sm min-w-0" />
        {submitted && <button type="button" onClick={() => { setQuery(""); setSubmitted(""); }} className="text-text-dim hover:text-text shrink-0"><X size={15} /></button>}
      </form>

      {!submitted && (
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((q) => (
            <button key={q} onClick={() => submit(q)} className="px-3 py-1.5 rounded-full text-caption font-medium border bg-surface text-text-muted hover:text-text hover:border-primary/40 transition-colors">{q}</button>
          ))}
        </div>
      )}

      {!submitted ? (
        <div className="rounded-xl border bg-surface p-6 flex flex-col items-center text-center" style={{ borderLeft: "3px solid var(--biome-mindvault)" }}>
          <Brain size={32} className="text-text-dim mb-3" strokeWidth={1.25} />
          <div className="text-body text-text-muted">Search the Mind Vault — Limitless lifelogs, collector transcripts, and personal notes.</div>
          <div className="text-caption text-text-dim mt-1">Pick a topic above or type a query.</div>
        </div>
      ) : (
        <McpCard title={`Knowledge · "${submitted}"${hits.length ? ` (${hits.length})` : ""}`} loading={res.loading} error={res.error} onRefresh={res.refresh} accentVar="--biome-mindvault">
          {hits.length === 0 ? (
            <div className="text-body-sm text-text-dim">No knowledge matched that query.</div>
          ) : (
            <div className="space-y-3">
              {hits.map((h, i) => (
                <button key={i} onClick={() => setChatContext({ label: `Knowledge: ${titleOf(h).slice(0, 44)}`, detail: `${titleOf(h)}\n${bodyOf(h)}${h.source ? `\n(source: ${h.source})` : ""}` })}
                  className="w-full text-left border-b border-border last:border-0 pb-3 last:pb-0 group">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={13} className="text-primary shrink-0" />
                    <span className="text-body-sm text-text font-medium leading-snug group-hover:text-primary transition-colors">{titleOf(h)}</span>
                  </div>
                  {bodyOf(h) && <div className="text-body-sm text-text-muted leading-relaxed line-clamp-3 pl-5">{bodyOf(h)}</div>}
                  <div className="flex items-center gap-2 mt-1 pl-5 text-caption text-text-dim">
                    {(h.source ?? h.type) && <span className="px-1.5 py-0.5 rounded-full bg-primary-muted text-primary">{h.source ?? h.type}</span>}
                    {tsLabel(h) && <span className="font-mono">{tsLabel(h)}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </McpCard>
      )}
    </PanelScroll>
  );
}
